import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authUtils } from '@/utils/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
  /** Single permission required to access this route */
  permission?: string;
  /** Multiple permissions — user needs at least one (or all if requireAll=true) */
  permissions?: string[];
  /** If true, user must have ALL listed permissions. Default: false (any) */
  requireAll?: boolean;
  /** Custom gate (e.g. bookkeeper work). Runs after auth; deny redirects. */
  authorize?: () => boolean;
  /** Where to redirect if user lacks permission (default: /dashboard) */
  permissionFallback?: string;
}

export default function ProtectedRoute({
  children,
  redirectTo = '/login',
  permission,
  permissions,
  requireAll = false,
  authorize,
  permissionFallback = '/dashboard',
}: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = authUtils.isAuthenticated();

  if (!isAuthenticated) {
    // Redirect to login with return URL
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Permission checks. Delegate to authUtils.hasPermission, which already
  // (a) grants everything to the owner / platform admin, and (b) handles BOTH
  // permission encodings (string array AND { permission: { name } } objects).
  // The previous inline `userPermissions.includes(...)` only matched the string
  // form, so users whose permissions came back as objects were wrongly bounced.
  if (authorize && !authorize()) {
    return <Navigate to={permissionFallback} replace />;
  }

  if (permission && !authUtils.hasPermission(permission)) {
    return <Navigate to={permissionFallback} replace />;
  }

  if (permissions && permissions.length > 0) {
    const ok = requireAll
      ? permissions.every((p) => authUtils.hasPermission(p))
      : permissions.some((p) => authUtils.hasPermission(p));
    if (!ok) return <Navigate to={permissionFallback} replace />;
  }

  return <>{children}</>;
}
